import { fetchClient } from "./api/fetching";
import {
  calculatePercentage,
  calculateSubtractedValues,
  calculateSum,
  round,
  decimalRound,
  getPercentageFromDecimal,
  IsMlsCompetition,
} from "../../libraries/_helpers";
import {
  apiURL,
  forgeClubDAPI,
  forgePlayerDAPI,
  culture,
} from "./api/variables";
import { enUS, fr, es } from "date-fns/locale";

export const test = "test";

export const statsApiHash = {
  appearances: "appearances",
  games_started: "game_started",
  games_played: "games_played",
  goals: "goals",
  minutes: "mins_played",
  expected_goals: "expected_goals",
  goal_assist: "goal_assist",
  assists: "goal_assist",
  expected_assists: "expected_assists",
  offsides: "total_offside",
  substitute_appearances: "total_sub_on",
  key_passes: "total_att_assist",
  won_tackles: "won_tackle",
  yellow_card: "yellow_card",
  successful_dribble: "successful_dribble",
  goals_inside_18: "att_ibox_goal",
  shots: "total_scoring_att",
  goals_outside_18: "att_obox_goal",
  penalty_kick_goals_for: "att_pen_goal",
  penalty_kick_attempts_for: "att_pen_taken",
  shots_on_target: "ontarget_scoring_att",
  passes_completed: "total_pass",
  passes_in_the_opposition_half: "accurate_fwd_zone_pass",
  passes_in_the_own_half: "accurate_back_zone_pass",
  short_passes: "successful_short_pass",
  penalty_kicks_against: "penalty_faced",
  penalty_kicks_saved: "penalty_save",
  passing_percentage: "accurate_pass_per",
  interceptions: "interception",
  clean_sheets: "clean_sheet",
  expected_goals_against: "expected_goals_conceded",
  goals_against: "goals_conceded",
  successful_crosses: "accurate_cross",
  long_passes: "accurate_long_balls",
  headed_goals: "att_hd_goal",
  red_card: "red_card",
  total_red_card: "total_red_card",
  passes_attempted: "total_pass",
  corner_kicks: "corner_taken",
};

export const statsApiPositionTypes = {
  GK: "Goalkeeper",
  LB: "Left Back",
  LCB: "Left Centre Back",
  CB: "Centre Back",
  RCB: "Right Centre Back",
  RB: "Right Back",
  WLDM: "Wide-Left Defensive Midfielder",
  LDM: "Left Defensive Midfielder",
  CDM: "Central Defensive Midfielder",
  RDM: "Right Defensive Midfielder",
  WRDM: "Wide-Right Defensive Midfielder",
  LM: "Left Midfielder",
  HLM: "Half-Left Midfielder",
  CM: "Central Midfielder",
  HRM: "Half-Right Midfielder",
  RM: "Right Midfielder",
  LAM: "Left Attacking Midfielder",
  HLAM: "Half-Left Attacking Midfielder",
  CAM: "Central Attacking Midfielder",
  HRAM: "Half-Right Attacking Midfielder",
  RAM: "Right Attacking Midfielder",
  SS: "Supporting Striker",
  LW: "Left Wide Player",
  LS: "Left Striker",
  CF: "Centre Forward",
  RS: "Right Striker",
  RW: "Right Wide Player",
};

export const statsApiFormationMatrix = {
  "3-1-4-2": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["CDM"],
    ["RM", "HRM", "HLM", "LM"],
    ["RS", "LS"],
  ],
  "3-3-2-2": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["CDM", "WRDM", "HRM"],
    ["HLM", "WLDM"],
    ["RS", "LS"],
  ],
  "3-3-2-2 double-10": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["CDM", "RM", "HRAM"],
    ["HLAM", "LM"],
    ["RS", "LS"],
  ],
  "3-3-3-1": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RDM", "CDM", "LDM"],
    ["RAM", "CAM", "LAM"],
    ["CF"],
  ],
  "3-4-1-2": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RM", "RDM", "LDM", "LM"],
    ["CAM"],
    ["RS", "LS"],
  ],
  "3-4-1-2 defensive midfielder": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["WRDM", "RDM", "LDM", "WLDM"],
    ["CAM"],
    ["RS", "LS"],
  ],
  "3-4-2-1": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RM", "RDM", "LDM", "LM"],
    ["RAM", "LAM"],
    ["CF"],
  ],
  "3-4-2-1 defensive midfielder": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["WRDM", "RDM", "LDM", "WLDM"],
    ["RAM", "LAM"],
    ["CF"],
  ],
  "3-4-2-1 double-10": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["WRDM", "RDM", "LDM", "WLDM"],
    ["HRAM", "HLM"],
    ["CF"],
  ],
  "3-4-3": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RM", "RDM", "LDM", "LM"],
    ["RW", "CF", "LW"],
  ],
  "3-4-3 defensive midfielder": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["WRDM", "RDM", "LDM", "WLDM"],
    ["RW", "CF", "LW"],
  ],
  "3-4-3 diamond": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["CDM", "RM", "LM", "CAM"],
    ["RW", "CF", "LW"],
  ],
  "3-5-1-1": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RM", "RDM", "CM", "LDM", "LM"],
    ["SS"],
    ["CF"],
  ],
  "3-5-1-1 defensive outliner": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["WRDM", "RDM", "CM", "LDM", "WLDM"],
    ["SS"],
    ["CF"],
  ],
  "3-5-2": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RM", "RDM", "CM", "LDM", "LM"],
    ["RS", "LS"],
  ],
  "3-5-2 attacking wingers": [
    ["GK"],
    ["RCB", "CB", "LCB"],
    ["RAM", "HRM", "CDM", "HLM", "LAM"],
    ["RS", "LS"],
  ],
  "4-1-3-2": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["CDM"],
    ["RAM", "CAM", "LAM"],
    ["RS", "LS"],
  ],
  "4-1-4-1": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["CDM"],
    ["RM", "HRM", "HLM", "LM"],
    ["CF"],
  ],
  "4-1-4-1 central double 10": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["CDM"],
    ["RM", "HRAM", "HLM", "LM"],
    ["CF"],
  ],
  "4-1-4-1 offensive midfielder": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["CDM"],
    ["RAM", "HRAM", "HLM", "LAM"],
    ["CF"],
  ],
  "4-2-3-1": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RDM", "LDM"],
    ["RAM", "CAM", "LAM"],
    ["CF"],
  ],
  "4-3-1-2": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RDM", "CDM", "LDM"],
    ["CAM"],
    ["RS", "LS"],
  ],
  "4-3-2-1": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RM", "CM", "LM"],
    ["RAM", "LAM"],
    ["CF"],
  ],
  "4-3-3": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["HRM", "CM", "HLM"],
    ["RW", "CF", "LW"],
  ],
  "4-3-3 double-6": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RDM", "CAM", "LDM"],
    ["RW", "CF", "LW"],
  ],
  "4-3-3 with 6": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["HRM", "CDM", "HLM"],
    ["RW", "CF", "LW"],
  ],
  "4-4-1-1": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RM", "RDM", "LDM", "LM"],
    ["SS"],
    ["CF"],
  ],
  "4-4-1-1 offensive outliner": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RAM", "RDM", "LAM"],
    ["SS"],
    ["CF"],
  ],
  "4-4-2 diamond": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["CDM", "HRM", "HLM", "CAM"],
    ["RS", "LS"],
  ],
  "4-4-2 double-6": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RM", "RDM", "LDM", "LM"],
    ["RS", "LS"],
  ],
  "4-4-2 double-6 offensive outliner": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RAM", "RDM", "LAM"],
    ["RS", "LS"],
  ],
  "4-5-1": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RM", "RDM", "CM", "LDM", "LM"],
    ["CF"],
  ],
  "4-5-1 offensive outliner": [
    ["GK"],
    ["RB", "RCB", "LCB", "LB"],
    ["RAM", "HRM", "CDM", "HLM", "LAM"],
    ["CF"],
  ],
  "5-3-1-1": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RM", "CM", "LM"],
    ["SS"],
    ["CF"],
  ],
  "5-3-1-1 with 6": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RM", "CDM", "LM"],
    ["SS"],
    ["CF"],
  ],
  "5-3-2": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RM", "CM", "LM"],
    ["RS", "LS"],
  ],
  "5-3-2 double-6": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RDM", "CAM", "LDM"],
    ["RS", "LS"],
  ],
  "5-3-2 with 6": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["HRM", "CDM", "HLM"],
    ["RS", "LS"],
  ],
  "5-4-1": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RM", "RDM", "LDM", "LM"],
    ["CF"],
  ],
  "5-4-1 diamond": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["HLM", "CDM", "CAM", "HRM"],
    ["CF"],
  ],
  "5-4-1 offensive outliner": [
    ["GK"],
    ["RB", "RCB", "CB", "LCB", "LB"],
    ["RAM", "RDM", "LAM"],
    ["CF"],
  ],
};

export const getFormationMatrix = (formation) => {
  const formationKey = formation?.toLowerCase();

  return statsApiFormationMatrix[formationKey];
};

export const statsApiCardTypes = {
  yellow: "yellow",
  red: "red",
  yellowRed: "yellowRed",
};

export const statsApiManagerTypes = {
  headCoach: "headcoach",
  assistantCoach: "assistantcoach",
  goalKeeperCoach: "goalkeepercoach",
};

export const getCultureLocalizer = () => {
  switch (culture.substr(0, 2)) {
    case "fr":
      return fr;
    case "es":
      return es;
    default:
      return enUS;
  }
};

export const entityTypes = {
  story: "story",
  customEntity: "customentity",
};

export const entityCodes = {
  brightcoveVideo: "brightcovevideo",
};

export const highlightTypes = {
  gameHighlights: "gameHighlights",
  condensedGame: "condensedGame",
  fullGame: "fullGame",
};

// TODO: We should update variables names with new broadcaster names.
export const broadcasterTypes = {
  usNational: "US TV",
  usStreaming: "US Streaming",
  canadianNational: "Canada TV",
  canadianStreaming: "Canada Streaming",
  canadaRadio: "Canada Radio",
  usRadio: "US Radio",
  linearTv: "Linear TV",
  radio: "Radio",
  streaming: "Streaming",
  internationalStreaming: "International Streaming",
};

export const svgSizes = {
  xSmall: "x-small",
  small: "small",
  medium: "medium",
  large: "large",
  xLarge: "xLarge",
};

export const roleTypes = {
  clubs: "clubs",
  players: "players",
};

export const cardTypes = {
  club: "club",
  player: "player",
};

export const filterTypes = {
  all: "All",
  allCompetitions: "All Competitions",
  allClubs: "All Clubs",
  allPositions: "All Positions",
};

export const playerPositionTypes = {
  all: "All",
  outfieldPlayers: "Outfield Players",
  defender: "Defender",
  forward: "Forward",
  goalkeeper: "Goalkeeper",
  midfielder: "Midfielder",
  substitute: "Substitute",
};

export const playerStatsPositionTypes = {
  all: "All",
  defender: "Defender",
  forward: "Forward",
  goalkeeper: "Goalkeeper",
  midfielder: "Midfielder",
};

export const translateTypes = (t, typeLabel) => {
  switch (typeLabel) {
    case "All Competitions":
      return t("match_all_competitions");
    case "All Clubs":
      return t("match_all_clubs");
    case "All":
      return t("match_all");
    case "Defender":
      return t("defender");
    case "Forward":
      return t("forward");
    case "Goalkeeper":
      return t("goalkeeper");
    case "Midfielder":
      return t("midfielder");
    case "Substitute":
      return t("substitute");
    case "Outfield Players":
      return t("outfield_players");
  }
  return typeLabel;
};

export const directionTypes = {
  prev: "prev",
  next: "next",
};

export const queryStatusTypes = {
  idle: "idle",
  loading: "loading",
  error: "error",
  success: "success",
};

export const mlsPhaseTypes = {
  regular: "Regular",
  cup: "Cup",
};

export const getEmptyStatTableValues = (count = 30) => {
  return {
    header: "",
    items: [...Array(count)].map(() => {
      return {
        player: null,
        club: null,
        appearances: null,
        game_started: null,
        games_played: null,
        total_sub_off: null,
        mins_played: null,
        goals: null,
        pass_percentage: null,
        total_assists: null,
        expected_assists: null,
        expected_goals_assists: null,
        key_passes: null,
        won_tackle: null,
        duel: null,
        ground_duel_percentage: null,
        dribbles_completed: null,
        dribbles_completed_percentage: null,
        fk_foul_lost: null,
        accurate_fwd_zone_pass: null,
        accurate_back_zone_pass: null,
        accurate_cross: null,
        goal_assist: null,
        total_att_assist: null,
        accurate_long_balls: null,
        att_ibox_goal: null,
        att_obox_goal: null,
        att_hd_goal: null,
        att_pen_goal: null,
        att_pen_taken: null,
        total_offside: null,
        yellow_card: null,
        total_red_card: null,
        successful_dribble: null,
      };
    }),
  };
};

export const getEmptyStatTableValuesAccessors = (
  count = 30,
  accessors = []
) => {
  return {
    header: "loading...",
    items: [...Array(count)].map(() => {
      const obj = {};
      accessors.forEach((accessor) => (obj[accessor] = null));
      return obj;
    }),
  };
};

export const formatImageUrl = (
  url,
  imageFormat = { format: "w_300,c_scale", extension: "f_auto" },
  isFallback = false
) => {
  if (!url && typeof url !== "string") {
    return;
  }

  const { format, extension } = imageFormat;

  return {
    highQuality: url.replace("{formatInstructions}", `${format}/${extension}`),
    lowQuality: url.replace(
      "{formatInstructions}",
      `${format}/${extension}/e_blur:1000,q_1,f_auto/e_grayscale/`
    ),
    title: "",
    isFallback,
  };
};

export function getSeasonsData(response) {
  if (!response) {
    return [];
  }

  return response.seasons.map((entry) => entry.season);
}

export function getForgeSeason(response, competitionOptaId) {
  if (!response || !response.items) {
    return [];
  }

  return response.items.find((competition) => {
    const { fields } = competition || {};
    const { competitionOptaId: optaId } = fields || {};

    return competitionOptaId
      ? optaId.toString() === competitionOptaId.toString()
      : null;
  });
}

export function getForgeVideos(response) {
  if (!response || !response.items) {
    return [];
  }

  return response.items;
}

export function getForgeCompetitionsSportecId(response, includeAll = false) {
  if (!response || !response.items) {
    return [];
  }

  const competitions = response.items
    .filter?.((competition) => !!competition.fields?.sportecId)
    ?.map((competition) => {
      const { fields, slug } = competition || {};
      const {
        sportecId,
        name,
        shortName,
        blockheaderName,
        nameOverwrite,
        mlsPhaseType,
      } = fields || {};

      return {
        sportecId,
        name,
        shortName: shortName ? shortName : name,
        blockheaderName,
        nameOverwrite,
        mlsPhaseType,
        slug,
      };
    });

  return includeAll
    ? [
        {
          sportecId: filterTypes.allCompetitions,
          name: filterTypes.allCompetitions,
          shortName: filterTypes.allCompetitions,
          mlsPhaseType: mlsPhaseTypes.regular,
          slug: "all-competitions",
        },
        ...competitions,
      ]
    : competitions;
}

export function formatForgeAllClubsData(clubsData) {
  if (!clubsData || !clubsData.items) {
    return [];
  }

  return clubsData.items
    .map((forgeClubData) => formatForgeClubData({}, forgeClubData.fields))
    .filter((club) => club?.optaId);
}

export function getClubsData(response, activeClubs = []) {
  if (!response) {
    return [];
  }

  const allClubs = {
    team_id: filterTypes.allClubs,
    team_name: filterTypes.allClubs,
    team_short_name: filterTypes.allClubs,
    team_three_letter_code: filterTypes.allClubs,
    backgroundColor: null,
    logoBWSlug: null,
    logoColorSlug: null,
    logoColorUrl: null,
    crestColorSlug: null,
    slug: null,
  };

  return [
    allClubs,
    ...response.team_statistics.filter((club) =>
      activeClubs?.length
        ? activeClubs.includes(club?.team_id?.toString())
        : club?.team_id
    ),
  ];
}

export function getPureClubsData(response) {
  if (!response) {
    return [];
  }

  return [
    ...response.filter((club) => club.club_name).map((club) => club.club_name),
  ];
}

export function getIdClubsData(response) {
  if (!response) {
    return [];
  }

  return [
    ...response
      .filter((club) => club.abbreviation && club.opta_id)
      .map((club) => {
        return { abbreviation: club.abbreviation, opta_id: club.opta_id };
      }),
  ];
}

export function getAdInsertPosition(arrayLength) {
  if (!arrayLength || arrayLength === 1) {
    return -1;
  }

  if (arrayLength % 2 === 0) {
    return arrayLength / 2;
  }

  return Math.round(arrayLength / 2);
}

export function getSeasonStatsData({ key, response, cardType }) {
  const sanitizeFn =
    cardType === cardTypes.club ? sanitizeClubStats : sanitizePlayerStats;

  return Promise.all(response.map((statsObj) => sanitizeFn(statsObj)))
    .then((data) => {
      return { header: key, items: data };
    })
    .catch((err) => {
      throw new Error(`getClubSeasonStatsData: ${err}`);
    });
}

export function getSeasonClubStatsData(header, clubsData) {
  if (!clubsData) {
    return {
      header: null,
      items: null,
    };
  }

  const sanitizedClubData = clubsData.map((club) => sanitizeClubStats(club));

  return {
    header,
    items: sanitizedClubData,
  };
}

export function formatForgePlayerData(mlsPlayer, forgePlayer, position = null) {
  if (!forgePlayer && !mlsPlayer) {
    return null;
  }

  const { fields, thumbnail, slug, selfUrl, position: forgePosition } =
    forgePlayer || {};
  const {
    firstName,
    lastName,
    biography,
    clubOptaId,
    fullName,
    knownName,
    optaId,
  } = fields || {};
  const {
    known_name,
    first_name,
    last_name,
    opta_id,
    full_name,
    birth_city,
    birth_country,
    birth_date,
    birth_state,
    height,
    weight,
    preferred_foot,
  } = mlsPlayer || {};

  return {
    birthCity: birth_city || null,
    birthCountry: birth_country || null,
    birthDate: birth_date || null,
    birthState: birth_state || null,
    preferredFoot: preferred_foot || null,
    weight,
    height,
    knownName: knownName || known_name || null,
    firstName: firstName || first_name || null,
    lastName: lastName || last_name || null,
    image: thumbnail || null,
    optaId: optaId || opta_id,
    biography: biography || null,
    clubOptaId: clubOptaId || null,
    fullName: fullName || full_name || null,
    selfUrl: selfUrl || null,
    slug: slug || null,
    isOptaKnownName: !knownName && known_name,
    position: forgePosition || position || null,
  };
}

export async function getForgePlayerData(player) {
  if (!player) {
    return null;
  }

  const forgePlayerEndpoint = forgePlayerDAPI(player.opta_id);
  const forgePlayerData = await fetchClient({
    key: `player-${player.opta_id ? player.opta_id : "no-opta-id"}`,
    endpoint: forgePlayerEndpoint,
    apiURL: apiURL.forgeDAPI,
  });
  const forgePlayer = forgePlayerData?.items?.[0];

  const { fields, thumbnail, slug, selfUrl } = forgePlayer || {};
  const {
    firstName,
    lastName,
    biography,
    clubOptaId,
    fullName,
    knownName,
    optaId,
  } = fields || {};

  return {
    knownName: knownName || null,
    firstName: firstName || player.first_name || null,
    lastName: lastName || player.last_name || null,
    image: thumbnail || null,
    optaId: optaId || player.opta_id,
    biography: biography || null,
    clubOptaId: clubOptaId || null,
    fullName: fullName || null,
    selfUrl: selfUrl || null,
    slug: slug || null,
  };
}

export function formatForgeClubData(mlsClubData, forgeClubData) {
  if (!forgeClubData && !mlsClubData) {
    return null;
  }

  const {
    abbreviation,
    backgroundColor,
    crestColorUrl,
    fullName,
    logoBWUrl,
    logoColorUrl,
    optaId,
    shortName,
    slug,
  } = forgeClubData || {};
  const {
    name: mlsName,
    abbreviation: mlsAbbreviation,
    opta_id,
    year_founded,
  } = mlsClubData || {};

  return {
    name: name || mlsName || null,
    abbreviation: abbreviation || mlsAbbreviation || null,
    shortName: shortName || null,
    fullName: fullName || null,
    logoColorUrl: logoColorUrl || null,
    logoBWUrl: logoBWUrl || null,
    sportecId: optaId || opta_id || null,
    backgroundColor: backgroundColor || null,
    crestColorUrl: crestColorUrl || null,
    yearFounded: year_founded || null,
    slug: slug || null,
  };
}

export async function getForgeClubData(club) {
  if (!club) {
    return null;
  }

  const forgeClubEndpoint = forgeClubDAPI(club.opta_id);
  const forgeClubData = await fetchClient({
    key: `forge-player-${club.opta_id ? club.opta_id : "no-opta-id"}`,
    endpoint: forgeClubEndpoint,
    apiURL: apiURL.forgeDAPI,
  });
  const forgeClub = forgeClubData?.items?.[0];

  const { fields, thumbnail, slug, selfUrl } = forgeClub || {};
  const { name, homeVenueOptaId, optaId, backgroundColor, shortName } =
    fields || {};

  return {
    name: shortName || name || club.name || null,
    abbreviation: club.abbreviation || null,
    image: thumbnail || null,
    optaId: optaId || club.opta_id,
    homeVenueOptaId: homeVenueOptaId || null,
    selfUrl: selfUrl || null,
    slug: slug || null,
    backgroundColor: backgroundColor || null,
  };
}

export function formatPlayerStats(statistics) {
  if (!statistics) {
    return null;
  }

  return {
    fouls_won: statistics?.fouls_suffered || 0,
    team_short_name: statistics?.team_short_name || null,
    team_three_letter_code: statistics?.team_three_letter_code || null,
    team_id: statistics?.team_id || null,
    team_name: statistics?.team_name || null,
    player_first_name: statistics?.player_first_name || null,
    player_last_name: statistics?.player_last_name || null,
    player_id: statistics?.player_id || null,
    sportecId: statistics?.player_id || null,
    player: {
      player_id: statistics?.player_id || null,
      firstName: statistics?.player_first_name || null,
      lastName: statistics?.player_last_name || null,
    },
    club: {
      name: statistics?.team_short_name || null,
      abbreviation: statistics?.team_three_letter_code || null,
    },
    club_abbreviation: statistics?.team_three_letter_code || null,
    accurate_back_zone_pass: statistics?.accurate_back_zone_pass || 0,
    accurate_cross_per: getPercentageFromDecimal(
      statistics?.crosses_conversion_rate || 0
    ),
    accurate_cross: statistics?.crosses_successful_sum || 0,
    accurate_fwd_zone_pass: statistics?.accurate_fwd_zone_pass || 0,
    accurate_long_balls:
      statistics?.passes_from_open_play_long_successful_ratio || 0,
    accurate_long_ball_per:
      statistics?.passes_from_open_play_long_successful_ratio || 0,
    accurate_pass_per: getPercentageFromDecimal(
      statistics?.passes_conversion_rate || 0
    ),
    accurate_pass: statistics?.passes_successful_sum || 0,
    accurate_shooting_per: statistics?.accurate_shot_rate || 0,
    aerial_duel_per: 0,
    aerial_duel: statistics?.tackling_games_air_sum || 0,
    aerial_duels: statistics?.aerial_duels || 0,
    aerial_won: statistics?.aerial_won || 0,
    appearances: statistics?.appearances || 0,
    assists: statistics?.first_and_second_assists || 0,
    assist_90:
      statistics?.first_and_second_assists /
        (statistics?.normalized_player_minutes / 90) || 0,
    att_freekick_total: statistics?.passes_and_crosses_from_standards_sum || 0,
    att_hd_goal: statistics?.penalties_successful || 0,
    att_hd_total: statistics?.shots_at_goal_head || 0,
    att_ibox_goal: statistics?.att_ibox_goal || 0,
    att_lf_total: statistics?.shots_at_goal_left_leg || 0,
    att_obox_goal: statistics?.att_obox_goal || 0,
    att_pen_goal: statistics?.penalties_successful || 0,
    att_pen_taken: statistics?.penalties_sum || 0,
    att_rf_total: statistics?.shots_at_goal_right_leg || 0,
    average_shot_distance_against:
      statistics?.average_shot_distance_against || 0,
    average_shot_distance: statistics?.average_shot_distance || 0,
    clean_sheet: statistics?.clean_sheets || 0,
    clearances: statistics?.defensive_clearances || 0,
    corner_taken: statistics?.corner_kicks_sum || 0,
    defensive_third_passes_against:
      statistics?.defensive_third_passes_against || 0,
    dribbles_completed_percentage:
      statistics?.dribbles_completed_percentage || 0,
    dribbles_completed: statistics?.dribbles_completed || 0,
    duel_lost: statistics?.duel_lost || 0,
    duel_won: statistics?.duel_won || 0,
    duel: statistics?.duel || 0,
    expected_assists: statistics?.expected_assists || 0,
    expected_goals_conceded: statistics?.expected_goals_conceded || 0,
    expected_goals: statistics?.xg || statistics?.xG || 0,
    final_third_passes_against: statistics?.final_third_passes_against || 0,
    fk_foul_lost: statistics?.fouls_sum || 0,
    fk_foul_won: statistics?.fouls_suffered || 0,
    fouls: statistics?.fouls || 0,
    game_started:
      statistics?.matches_played - statistics?.substitutions_in < 0
        ? 0
        : statistics?.matches_played - statistics?.substitutions_in,
    games_played: statistics?.matches_played || 0,
    goal_assist: IsMlsCompetition(statistics?.competition_id)
      ? statistics?.first_and_second_assists
      : statistics?.goal_assist || 0,
    goal_kicks: statistics?.goal_kicks || 0,
    goal_conversion_per: statistics?.shots_conversion_rate || 0,
    goals_90: 0,
    goals_plus_assists:
      statistics?.goals_plus_first_and_second_assists ||
      statistics?.goals_plus_assists ||
      0,
    goals_assists:
      statistics?.first_and_second_assists || statistics?.assists || 0,
    goals_conceded: statistics?.goals_conceded || 0,
    goal_differential: statistics?.goal_differential || 0,
    goals: statistics?.goals || 0,
    interception: statistics?.interceptions_sum || 0,
    interceptions: statistics?.interceptions_sum || 0,
    keeper_throws: statistics?.keeper_throws || 0,
    key_passes: statistics?.assists_shot_at_goal || 0,
    lost_corners: statistics?.lost_corners || 0,
    long_ball_percentage:
      statistics?.passes_from_open_play_long_successful_ratio || 0,
    midfield_third_passes_against:
      statistics?.midfield_third_passes_against || 0,
    mins_played: statistics?.normalized_player_minutes || 0,
    ontarget_scoring_att: statistics?.shots_on_target || 0,
    shots_on_target: statistics?.shots_on_target || 0,
    pass_percentage: statistics?.pass_percentage || 0,
    passing_percentages_against: statistics?.passing_percentages_against || 0,
    penalty_conceded: statistics?.penalty_conceded || 0,
    pen_goals_conceded: statistics?.pen_goals_conceded || 0,
    penalty_faced: statistics?.penalties_faced || 0,
    penalty_save: statistics?.penalties_saved || 0,
    penalty_saved: statistics?.penalties_saved || 0,
    pressing_actions: statistics?.pressing_actions || 0,
    punches: statistics?.punches || 0,
    red_card: statistics?.red_card || 0,
    saves_percentage: statistics?.saves_percentage || 0,
    saves: statistics?.goalkeeper_saves || 0,
    shots_on_target_against: statistics?.shots_on_target_against || 0,
    successful_dribble: statistics?.successful_dribble || 0,
    successful_passes: statistics?.successful_passes || 0,
    successful_short_pass:
      statistics?.passes_from_open_play_short_successful_ratio || 0,
    total_assists: statistics?.total_assists || 0,
    total_att_assist: statistics?.assists_shot_at_goal || 0,
    total_clearance: statistics?.defensive_clearances || 0,
    total_contest: statistics?.total_contest || 0,
    total_draws: statistics?.total_draws || 0,
    total_matches: statistics?.matches_played || 0,
    total_launches: statistics?.total_launches || 0,
    total_long_balls: statistics?.total_long_balls || 0,
    total_losses: statistics?.total_losses || 0,
    total_offside: statistics?.offsides || 0,
    total_pass: statistics?.passes_sum || 0,
    total_starts: statistics?.matches_started || 0,
    total_red_card: statistics?.cards_red || 0,
    total_scoring_att: statistics?.shots_at_goal_sum || 0,
    total_sub_off: statistics?.total_sub_off || 0,
    total_sub_on: statistics?.substitutions_in || 0,
    total_wins: statistics?.total_wins || 0,
    touches: statistics?.ball_actions || 0,
    turnover: statistics?.turnover || 0,
    was_fouled: statistics?.was_fouled || 0,
    won_tackle: statistics?.won_tackle || 0,
    yellow_card: statistics?.cards_yellow || 0,
  };
}

export async function sanitizePlayerStats(statsObj) {
  if (!statsObj) {
    return null;
  }

  const player = await getForgePlayerData(statsObj?.player);
  const club = await getForgeClubData(statsObj?.club);
  const statistics = statsObj && statsObj.statistics;
  const dribble_percentage = calculatePercentage(
    statistics?.successful_dribble,
    statistics?.successful_dribble + statistics?.failed_dribble
  );
  const expected_assists_minus_assists = calculateSubtractedValues(
    statistics?.expected_assists,
    statistics?.goal_assist
  );
  const launches_attempted = calculateSum(
    statistics?.accurate_Launches,
    statistics?.failed_launches
  );
  const expected_goals_minus_goals = calculateSubtractedValues(
    statistics?.expected_goals,
    statistics?.goals
  );
  const att_pen_percentage = calculatePercentage(
    statistics?.att_pen_goal,
    statistics?.att_pen_taken
  );
  const goals_assists = calculateSum(
    statistics?.goals,
    statistics?.goal_assist
  );
  const expected_goals_assists = calculateSum(
    statistics?.expected_goals,
    statistics?.expected_assists
  );
  const shots_against = calculateSum(
    statistics?.attempts_conceded_ibox,
    statistics?.attempts_conceded_obox
  );
  const goals_against_expected_goals_against = calculateSubtractedValues(
    statistics?.goals_against,
    statistics?.expected_goals_against
  );
  const long_ball_percentage = calculatePercentage(
    statistics?.accurate_long_balls,
    statistics?.accurate_long_balls + statistics?.failed_long_balls
  );
  const save_percentage = calculatePercentage(
    statistics?.goals_conceded,
    statistics?.attempts_conceded_ibox + statistics?.attempts_conceded_obox
  );
  const headed_duel_percentage = calculatePercentage(
    statistics?.aerial_won,
    statistics?.aerial_won + statistics?.aerial_lost
  );
  const ground_duel_percentage = calculatePercentage(
    statistics?.ground_duel_won,
    statistics?.ground_duel_won + statistics?.ground_duel_lost
  );
  const duel_percentage = calculatePercentage(
    statistics?.duel_won,
    statistics?.duel
  );
  const penalty_kick_save_percentage = calculatePercentage(
    statistics?.penalty_save,
    statistics?.penalty_conceded
  );
  const assist_90 =
    !isNaN(statistics?.goal_assist) && !isNaN(statistics?.mins_played)
      ? round(statistics?.goal_assist / (statistics?.mins_played / 90), 2)
      : 0;
  const goals_90 =
    !isNaN(statistics?.goals) && !isNaN(statistics?.mins_played)
      ? round(statistics?.goals / (statistics?.mins_played / 90), 2)
      : 0;
  const shot_conversion =
    !isNaN(statistics?.goals) && !isNaN(statistics?.total_scoring_att)
      ? calculatePercentage(statistics?.goals, statistics?.total_scoring_att)
      : 0;
  const goals_allowed_average =
    !isNaN(statistics?.goals_conceded) && !isNaN(statistics?.mins_played)
      ? round((statistics?.goals_conceded / statistics?.mins_played) * 90, 2)
      : 0;
  const save_percentage_gk = calculatePercentage(
    statistics?.saves,
    calculateSum(statistics?.saves, statistics?.goals_conceded)
  );
  const win_percentage = calculatePercentage(
    statistics?.total_wins,
    statistics?.matchesStarted
  );

  return {
    player,
    club,
    assist_90,
    goals_90,
    shot_conversion,
    goals_allowed_average,
    save_percentage_gk,
    appearances: statistics?.appearances || 0,
    game_started: statistics?.game_started || 0,
    games_played: statistics?.games_played || 0,
    total_sub_off: statistics?.total_sub_off || 0,
    mins_played: statistics?.mins_played || 0,
    goals: statistics?.goals || 0,
    goals_assists,
    expected_goals: statistics?.expected_goals || 0,
    expected_goals_minus_goals,
    expected_assists_minus_assists,
    launches_attempted,
    att_pen_percentage,
    accurate_shooting_per: decimalRound(
      statistics?.accurate_shooting_per || 0,
      1
    ),
    total_scoring_att: statistics?.total_scoring_att || 0,
    average_shot_distance: statistics?.average_shot_distance || 0,
    ontarget_scoring_att: statistics?.shots_on_target || 0,
    pass_percentage: statistics?.pass_percentage || 0,
    total_assists: statistics?.total_assists || 0,
    expected_assists: statistics?.expected_assists || 0,
    expected_goals_assists,
    key_passes: statistics?.key_passes || 0,
    won_tackle: statistics?.won_tackle || 0,
    duel: statistics?.duel || 0,
    duel_won: statistics?.duel_won || 0,
    duel_lost: statistics?.duel_lost || 0,
    duel_percentage,
    ground_duel_percentage,
    dribbles_completed: statistics?.dribbles_completed || 0,
    dribbles_completed_percentage:
      statistics?.dribbles_completed_percentage || 0,
    fk_foul_lost: statistics?.fk_foul_lost || 0,
    fk_foul_won: statistics?.fk_foul_won || 0,
    accurate_fwd_zone_pass: statistics?.accurate_fwd_zone_pass || 0,
    accurate_back_zone_pass: statistics?.accurate_back_zone_pass || 0,
    accurate_cross: statistics?.accurate_cross || 0,
    goal_assist: statistics?.goal_assist || 0,
    total_att_assist: statistics?.total_att_assist || 0,
    accurate_long_balls: statistics?.accurate_long_balls || 0,
    successful_short_pass: statistics?.successful_short_pass || 0,
    att_ibox_goal: statistics?.att_ibox_goal || 0,
    att_obox_goal: statistics?.att_obox_goal || 0,
    att_hd_goal: statistics?.att_hd_goal || 0,
    att_pen_goal: statistics?.att_pen_goal || 0,
    att_pen_taken: statistics?.att_pen_taken || 0,
    total_offside: statistics?.total_offside || 0,
    yellow_card: statistics?.yellow_card || 0,
    red_card: statistics?.red_card || 0,
    total_red_card: calculateSum(
      statistics?.total_red_card || 0,
      statistics?.cards_yellow_red || 0
    ),
    successful_dribble: statistics?.successful_dribble || 0,
    dribble_percentage,
    penalty_faced: statistics?.penalty_faced || 0,
    penalty_save: statistics?.penalty_save || 0,
    accurate_pass_per: round(statistics?.accurate_pass_per || 0, 1),
    interception: statistics?.interception || 0,
    clean_sheet: statistics?.clean_sheet || 0,
    expected_goals_conceded: statistics?.expected_goals_conceded || 0,
    goals_conceded: statistics?.goals_conceded || 0,
    total_clearance: statistics?.total_clearance || 0,
    turnover: statistics?.turnover || 0,
    total_pass: statistics?.total_pass || 0,
    pen_goals_conceded: statistics?.pen_goals_conceded || 0,
    penalty_kick_save_percentage,
    defensive_third_passes_against:
      statistics?.defensive_third_passes_against || 0,
    midfield_third_passes_against:
      statistics?.midfield_third_passes_against || 0,
    final_third_passes_against: statistics?.final_third_passes_against || 0,
    interceptions: statistics?.interception || 0,
    pressing_actions: statistics?.pressing_actions || 0,
    passing_percentages_against: statistics?.passing_percentages_against || 0,
    headed_duel_percentage,
    save_percentage,
    average_shot_distance_against:
      statistics?.average_shot_distance_against || 0,
    shots_on_target_against: statistics?.shots_on_target_against || 0,
    saves: statistics?.saves || 0,
    saves_percentage: statistics?.saves_percentage || 0,
    long_ball_percentage,
    shots_against,
    goals_against_expected_goals_against,
    clearances: statistics?.clearances || 0,
    total_wins: statistics?.total_wins || 0,
    total_losses: statistics?.total_losses || 0,
    total_draws: statistics?.total_draws || 0,
    win_percentage,
    att_hd_total: statistics?.att_hd_total || 0,
    att_freekick_total: statistics?.att_freekick_total || 0,
    att_lf_total: statistics?.att_lf_total || 0,
    att_rf_total: statistics?.att_rf_total || 0,
    touches: statistics?.touches || 0,
    aerial_won: statistics?.aerial_won || 0,
    aerial_duel_per: calculatePercentage(
      statistics?.aerial_won,
      statistics?.aerial_duel
    ),
    accurate_cross_per: statistics?.accurate_cross_per || 0,
  };
}

export function formatClubStats(statsObj, forgeClubData) {
  if (!statsObj) {
    return null;
  }

  const club = forgeClubData?.club;
  const statistics = forgeClubData?.statistics;
  const penalty_kick_save_percentage = calculatePercentage(
    statistics?.penalties_saved,
    calculateSum(statistics?.penalties_saved, statistics?.penalties_caused)
  );
  const expected_goals_minus_against = calculateSubtractedValues(
    statistics?.xG,
    statistics?.xG_efficiency
  );
  const expected_goals_minus_goals = calculateSubtractedValues(
    statistics?.expected_goals,
    statistics?.goals
  );
  const att_pen_percentage = calculatePercentage(
    statistics?.att_pen_goal,
    statistics?.att_pen_taken
  );
  const shots_against = calculateSum(
    statistics?.attempts_conceded_ibox,
    statistics?.attempts_conceded_obox
  );
  const expected_assists_minus_assists = calculateSubtractedValues(
    statistics?.expected_assists,
    statistics?.goal_assist
  );
  const pass_percentage = calculatePercentage(
    statistics?.passes_successful_sum,
    statistics?.passes_sum
  );
  const long_ball_percentage = calculatePercentage(
    statistics?.accurate_long_balls,
    statistics?.accurate_long_balls + statistics?.failed_long_balls
  );
  const save_percentage = calculatePercentage(
    statistics?.goals_conceded,
    statistics?.attempts_conceded_ibox + statistics?.attempts_conceded_obox
  );
  const headed_duel_percentage = calculatePercentage(
    statistics?.aerial_won,
    statistics?.aerial_won + statistics?.aerial_lost
  );
  const ground_duel_percentage = calculatePercentage(
    statistics?.ground_duel_won,
    statistics?.ground_duel_won + statistics?.ground_duel_lost
  );
  const duel_percentage = calculatePercentage(
    statistics?.duel_won,
    statistics?.duel
  );
  const total_goal_differential = calculateSubtractedValues(
    statistics?.goals,
    statistics?.goals_conceded
  );

  return {
    assists: statistics?.first_and_second_assists,
    passes_completed: statistics?.passes_successful_sum,
    corner_kicks: statistics?.corner_kicks_sum,
    club,
    att_pen_percentage,
    shots_against,
    long_ball_percentage,
    pass_percentage,
    ground_duel_percentage,
    duel_percentage,
    total_goal_differential,
    clean_sheet: statistics?.clean_sheet || 0,
    accurate_pass: statistics?.accurate_pass || 0,
    total_pass: statistics?.total_pass || 0,
    expected_assists_minus_assists,
    save_percentage,
    average_shot_distance: statistics?.average_shot_distance || 0,
    total_scoring_att: statistics?.total_scoring_att || 0,
    ontarget_scoring_att: statistics?.ontarget_scoring_att || 0,
    accurate_shooting_per: decimalRound(
      statistics?.accurate_shooting_per || 0,
      1
    ),
    appearances: statistics?.appearances || 0,
    game_started: statistics?.game_started || 0,
    goals_conceded: statistics?.goals_conceded || 0,
    total_clearance: statistics?.total_clearance || 0,
    expected_goals_conceded: statistics?.expected_goals_conceded || 0,
    expected_goals_minus_against,
    expected_goals_minus_goals,
    penalty_conceded: statistics?.penalty_conceded || 0,
    total_sub_off: statistics?.total_sub_off || 0,
    mins_played: statistics?.mins_played || 0,
    goals: statistics?.goals || 0,
    turnover: statistics?.turnover || 0,
    total_assists: statistics?.total_assists || 0,
    expected_goals_assists: statistics?.expected_goals_assists || 0,
    won_tackle: statistics?.won_tackle || 0,
    interception: statistics?.interception || 0,
    duel: statistics?.duel || 0,
    duel_won: statistics?.duel_won || 0,
    duel_lost: statistics?.duel_lost || 0,
    dribbles_completed: statistics?.dribbles_completed || 0,
    dribbles_completed_percentage:
      statistics?.dribbles_completed_percentage || 0,
    fk_foul_lost: statistics?.fk_foul_lost || 0,
    accurate_fwd_zone_pass: statistics?.accurate_fwd_zone_pass || 0,
    accurate_back_zone_pass: statistics?.accurate_back_zone_pass || 0,
    accurate_cross: statistics?.accurate_cross || 0,
    total_att_assist: statistics?.total_att_assist || 0,
    accurate_long_balls: statistics?.accurate_long_balls || 0,
    successful_short_pass: statistics?.successful_short_pass || 0,
    att_ibox_goal: statistics?.att_ibox_goal || 0,
    att_obox_goal: statistics?.att_obox_goal || 0,
    att_hd_goal: statistics?.att_hd_goal || 0,
    att_pen_goal: statistics?.att_pen_goal || 0,
    att_pen_taken: statistics?.att_pen_taken || 0,
    total_offside: statistics?.total_offside || 0,
    red_card: statistics?.red_card || 0,
    total_red_card: statistics?.total_red_card || 0,
    successful_dribble: statistics?.successful_dribble || 0,
    shots_on_target_against: statistics?.shots_on_target_against || 0,
    average_shot_distance_against:
      statistics?.average_shot_distance_against || 0,
    clearances: statistics?.clearances || 0,
    headed_duel_percentage,
    penalty_faced: statistics?.penalty_faced || 0,
    penalty_save: statistics?.penalty_save || 0,
    pen_goals_conceded: statistics?.pen_goals_conceded || 0,
    penalty_kick_save_percentage,
    pressing_actions: statistics?.pressing_actions || 0,
    passing_percentages_against: statistics?.passing_percentages_against || 0,
    defensive_third_passes_against:
      statistics?.defensive_third_passes_against || 0,
    midfield_third_passes_against:
      statistics?.midfield_third_passes_against || 0,
    final_third_passes_against: statistics?.final_third_passes_against || 0,
    saves: statistics?.saves || 0,
    att_hd_total: statistics?.att_hd_total || 0,
    fk_foul_won: statistics?.fk_foul_won || 0,
    att_freekick_total: statistics?.att_freekick_total || 0,
    interceptions: statistics?.interceptions || 0,
    att_lf_total: statistics?.att_lf_total || 0,
    att_rf_total: statistics?.att_rf_total || 0,
    touches: statistics?.touches || 0,
    aerial_won: statistics?.aerial_won || 0,
    aerial_duel_per: calculatePercentage(
      statistics?.aerial_won,
      statistics?.aerial_duel
    ),
    accurate_cross_per: statistics?.accurate_cross_per || 0,
    corner_taken: statistics?.corner_kicks_sum || 0,
    red_card: statistics?.cards_red || 0,
    club_id: statistics?.sportecId || 0,
    clubSportecId: statistics?.sportecId || 0,
    accurate_pass: statistics?.passes_and_crosses_successful_sum || 0,
    expected_goals: statistics?.xG || 0,
    expected_assists: statistics?.passes_conversion_rate || 0,
    games_played: statistics?.matches_played || 0,
    goal_assist:
      statistics?.first_and_second_assists || statistics?.assists || 0,
    key_passes: statistics?.goal_opportunities || 0,
    yellow_card: statistics?.cards_yellow || 0,
    total_matches: statistics?.matches_played || 0,
  };
}

export function sanitizeClubStats(statistics, mlsPhaseType) {
  if (!statistics) {
    return null;
  }

  const penalty_kick_save_percentage = calculatePercentage(
    statistics?.penalties_saved,
    statistics?.penalties_sum
  );
  const expected_goals_minus_against = calculateSubtractedValues(
    statistics?.expected_goals,
    statistics?.expected_goals_conceded
  );
  const expected_goals_minus_goals = calculateSubtractedValues(
    statistics?.expected_goals,
    statistics?.goals
  );
  const expected_assists_minus_assists = calculateSubtractedValues(
    statistics?.expected_assists,
    statistics?.goal_assist
  );
  const pass_percentage = calculatePercentage(
    statistics?.passes_successful_sum,
    statistics?.passes_sum
  );
  const save_percentage = calculatePercentage(
    statistics?.shots_faced,
    statistics?.goalkeeper_saves
  );
  const headed_duel_percentage = calculatePercentage(
    statistics?.aerial_won,
    statistics?.aerial_won + statistics?.aerial_lost
  );
  const ground_duel_percentage = calculatePercentage(
    statistics?.ground_duel_won,
    statistics?.ground_duel_won + statistics?.ground_duel_lost
  );
  const duel_percentage = calculatePercentage(
    statistics?.duel_won,
    statistics?.duel
  );
  const total_goal_differential = calculateSubtractedValues(
    statistics?.goals,
    statistics?.goals_conceded
  );

  return {
    accurate_back_zone_pass: statistics?.accurate_back_zone_pass || 0,
    accurate_cross_per: statistics?.accurate_cross_per || 0,
    accurate_cross: statistics?.accurate_cross || 0,
    accurate_fwd_zone_pass: statistics?.accurate_fwd_zone_pass || 0,
    accurate_long_balls: statistics?.crosses_successful_sum || 0,
    accurate_pass: statistics?.passes_successful_sum || 0,
    accurate_shooting_per: calculatePercentage(
      statistics?.shots_on_target,
      statistics?.shots_at_goal_sum
    ),
    aerial_duel_per: calculatePercentage(
      statistics?.tackling_games_air_won,
      statistics?.tackling_games_air_sum
    ),
    aerial_won: statistics?.aerial_won || 0,
    appearances: statistics?.appearances || 0,
    att_freekick_total: statistics?.free_kicks_sum || 0,
    att_hd_goal: statistics?.att_hd_goal || 0,
    att_hd_total: statistics?.att_hd_total || 0,
    att_ibox_goal: statistics?.att_ibox_goal || 0,
    att_lf_total: statistics?.att_lf_total || 0,
    att_obox_goal: statistics?.att_obox_goal || 0,
    att_pen_goal: statistics?.penalties_successful || 0,
    att_pen_percentage: getPercentageFromDecimal(
      statistics?.penalty_conversion_rate || 0
    ),
    att_pen_taken: statistics?.penalties_sum || 0,
    att_rf_total: statistics?.att_rf_total || 0,
    average_shot_distance_against:
      statistics?.average_shot_distance_against || 0,
    average_shot_distance: statistics?.average_shot_distance || 0,
    clean_sheet: statistics?.clean_sheets || 0,
    clearances: statistics?.defensive_clearances || 0,
    club_id: statistics?.team_id || statsObj?.club?.team_id,
    club: statistics?.club,
    corner_taken: statistics?.corner_kicks_sum || 0,
    defensive_third_passes_against:
      statistics?.defensive_third_passes_against || 0,
    dribbles_completed_percentage:
      statistics?.dribbles_completed_percentage || 0,
    dribbles_completed: statistics?.dribbles_completed || 0,
    duel_lost: statistics?.duel_lost || 0,
    duel_percentage,
    duel_won: statistics?.duel_won || 0,
    duel: statistics?.duel || 0,
    expected_assists_minus_assists,
    expected_assists: statistics?.passes_conversion_rate || 0,
    expected_goals_assists: statistics?.expected_goals_assists || 0,
    expected_goals_conceded: statistics?.expected_goals_conceded || 0,
    expected_goals_minus_against,
    expected_goals_minus_goals,
    expected_goals: statistics?.xG || 0,
    final_third_passes_against: statistics?.final_third_passes_against || 0,
    fk_foul_lost: statistics?.fouls_against_opponent || 0,
    fk_foul_won: statistics?.fouls_suffered || 0,
    game_started: statistics?.game_started || 0,
    games_played: statistics?.matches_played || 0,
    goal_assist: statistics?.assists || 0,
    goals_conceded: statistics?.goals_conceded || 0,
    goals: statistics?.goals || 0,
    ground_duel_percentage,
    headed_duel_percentage,
    interception: statistics?.interception || 0,
    interceptions: statistics?.interceptions || 0,
    key_passes: statistics?.goal_opportunities || 0,
    long_ball_percentage:
      statistics?.passes_from_open_play_long_successful_ratio || 0,
    midfield_third_passes_against:
      statistics?.midfield_third_passes_against || 0,
    mins_played: statistics?.mins_played || 0,
    ontarget_scoring_att: statistics?.shots_on_target || 0,
    pass_percentage,
    passing_percentages_against: statistics?.passing_percentages_against || 0,
    pen_goals_conceded: statistics?.pen_goals_conceded || 0,
    penalty_conceded: statistics?.penalties_caused || 0,
    penalty_faced: statistics?.penalties_sum || 0,
    penalty_kick_save_percentage,
    penalty_save: statistics?.penalties_saved || 0,
    pressing_actions: statistics?.pressing_actions || 0,
    red_card: statistics?.cards_red || 0,
    save_percentage,
    saves: statistics?.saves || 0,
    shots_against: statistics?.shots_faced || 0,
    shots_on_target_against: statistics?.shots_on_target_against || 0,
    successful_dribble: statistics?.successful_dribble || 0,
    successful_short_pass: statistics?.successful_short_pass || 0,
    team_id: statistics?.team_id || statistics?.club?.team_id,
    total_assists: statistics?.first_and_second_assists || 0,
    total_att_assist: statistics?.second_assists || 0,
    total_clearance: statistics?.defensive_clearances || 0,
    total_goal_differential,
    total_matches: statistics?.matches_played || 0,
    total_offside: statistics?.offsides || 0,
    total_pass: statistics?.passes_sum || 0,
    total_red_card: statistics?.cards_red || 0,
    total_scoring_att: statistics?.shots_at_goal_sum || 0,
    total_sub_off: statistics?.total_sub_off || 0,
    touches: statistics?.touches || 0,
    turnover: statistics?.turnover || 0,
    won_tackle: statistics?.won_tackle || 0,
    yellow_card: statistics?.cards_yellow || 0,
  };
}

export function mapStatTypeToStatsApi(statType) {
  const statsApiHash = {
    accurate_pass: "accurate_pass",
    aerial_duels_won: "aerial_won",
    appearances: "appearances",
    assists: "first_and_second_assists",
    clean_sheets: "clean_sheet",
    clearances: "total_clearance",
    corner_taken: "corner_taken",
    expected_assists: "expected_assists",
    expected_goals_against: "expected_goals_conceded",
    expected_goals: "expected_goals",
    fouls_won: "fouls_suffered",
    free_kicks: "att_freekick_total",
    games_played: "games_played",
    games_started: "game_started",
    goal_assist: "goal_assist",
    goalkeeper_saves: "saves",
    goals_against: "goals_conceded",
    goals_inside_18: "att_ibox_goal",
    goals_outside_18: "att_obox_goal",
    goals: "goals",
    headed_goals: "att_hd_goal",
    headed_shots: "att_hd_total",
    interceptions: "interception",
    key_passes: "assists_shot_at_goal",
    left_footed_shots: "att_lf_total",
    long_passes: "accurate_long_balls",
    minutes: "mins_played",
    offsides: "total_offside",
    passes_attempted: "total_pass",
    passes_completed: "total_pass",
    passes_in_the_opposition_half: "accurate_fwd_zone_pass",
    passes_in_the_own_half: "accurate_back_zone_pass",
    passing_percentage: "accurate_pass_per",
    penalty_kick_attempts_for: "att_pen_taken",
    penalty_kick_goals_for: "att_pen_goal",
    penalty_kicks_against: "penalty_faced",
    penalty_kicks_saved: "penalty_save",
    player_interception: "interception",
    red_card: "red_card",
    right_footed_shots: "att_rf_total",
    short_passes: "successful_short_pass",
    shots_assists: "total_att_assist",
    shots_on_target: "shots_on_target",
    shots: "shots_at_goal_sum",
    substitute_appearances: "substitution_in",
    successful_crosses: "accurate_cross",
    successful_dribble: "successful_dribble",
    total_red_card: "total_red_card",
    touches: "ball_actions",
    won_tackles: "won_tackle",
    yellow_card: "yellow_card",
    corner_kicks: "corner_taken",
  };

  if (statType in statsApiHash) {
    return {
      key: statType,
      value: statsApiHash[statType],
    };
  }
  return null;
}

export const checkNextPage = (items, itemCount = 30) => {
  if (items) {
    return items.length === itemCount;
  }

  return false;
};

export const countryTypes = {
  unitedStates: "US",
  canada: "CA",
};

export function composeGeoLocatedProviders(providers, country) {
  const geoObj = {
    national: [],
    streaming: [],
    radio: [],
    allProviders: [],
    international: [],
  };

  if (providers == null || country == null) {
    return geoObj;
  }

  if (Array.isArray(providers?.allProvidersList)) {
    const internationalProviders = providers.allProvidersList.filter(
      (provider) =>
        provider.broadcasterType === broadcasterTypes.internationalStreaming
    );

    geoObj.international = internationalProviders;
  }

  const {
    canadianStreaming,
    canadianNational,
    usStreaming,
    usNational,
    radio,
  } = providers || {};

  switch (country) {
    case countryTypes.unitedStates:
      if (usNational != null && usNational.length > 0) {
        geoObj.national = usNational;
        geoObj.allProviders = [...usNational];
      }
      if (usStreaming != null && usStreaming.length > 0) {
        geoObj.streaming = usStreaming;
        geoObj.allProviders = [...geoObj.allProviders, ...usStreaming];
      }
      if (radio != null && radio.length > 0) {
        geoObj.radio = radio.filter(
          (broadcaster) =>
            broadcaster?.broadcasterType === broadcasterTypes.usRadio
        );
        geoObj.allProviders = [...geoObj.allProviders, ...radio];
      }
      break;
    case countryTypes.canada:
      if (canadianNational != null && canadianNational.length > 0) {
        geoObj.national = canadianNational;
        geoObj.allProviders = [...canadianNational];
      }
      if (canadianStreaming != null && canadianStreaming.length > 0) {
        geoObj.streaming = canadianStreaming;
        geoObj.allProviders = [...geoObj.allProviders, ...canadianStreaming];
      }
      if (radio != null && radio.length > 0) {
        geoObj.radio = radio.filter(
          (broadcaster) =>
            broadcaster?.broadcasterType === broadcasterTypes.canadaRadio
        );
        geoObj.allProviders = [...geoObj.allProviders, ...radio];
      }
      break;
    default:
      if (usNational != null && usNational.length > 0) {
        geoObj.national = usNational;
        geoObj.allProviders = [...usNational];
      }
      if (usStreaming != null && usStreaming.length > 0) {
        geoObj.streaming = usStreaming;
        geoObj.allProviders = [...geoObj.allProviders, ...usStreaming];
      }
      if (canadianNational != null && canadianNational.length > 0) {
        geoObj.national = [...geoObj.national, ...canadianNational];
        geoObj.allProviders = [...geoObj.allProviders, ...canadianNational];
      }
      if (canadianStreaming != null && canadianStreaming.length > 0) {
        geoObj.streaming = [...geoObj.streaming, ...canadianStreaming];
        geoObj.allProviders = [...geoObj.allProviders, ...canadianStreaming];
      }
      if (radio != null && radio.length > 0) {
        geoObj.radio = radio;
        geoObj.allProviders = [...geoObj.allProviders, ...radio];
      }
  }

  return geoObj;
}

export function stringifyGeoProviders(providers) {
  if (providers == null || providers.length === 0) {
    return null;
  }

  return providers
    .map((provider) => {
      const { broadcasterShortName, broadcasterName } = provider || {};
      return broadcasterShortName ? broadcasterShortName : broadcasterName;
    })
    .join(", ");
}

export function stringifyProviders(providers) {
  if (providers == null || providers.length === 0) {
    return null;
  }

  return providers
    .map((provider) => {
      const { broadcasterShortName, broadcasterName } = provider || {};
      return broadcasterShortName ? broadcasterShortName : broadcasterName;
    })
    .join(", ");
}

function composeStatistics(statsObj, phase) {
  if (
    statsObj == null ||
    (statsObj.statistics == null &&
      statsObj.regular_season_statistics == null &&
      statsObj.postseason_statistics == null)
  ) {
    return null;
  }

  if (phase === mlsPhaseTypes.regular) {
    // Added this small fix for MNP
    // When it's MNP the .statistics prop is populated but the phase is Regular
    // regular_season_statistics is null in those cases
    if (!statsObj.regular_season_statistics && statsObj.statistics) {
      return statsObj.statistics;
    }

    return statsObj.regular_season_statistics;
  }

  if (phase === mlsPhaseTypes.cup) {
    return statsObj.postseason_statistics;
  }

  return statsObj.statistics;
}

export const matchHighlightsTagSlug = "match-highlights";

export const appleTvTypes = {
  appleTv: "appletv",
  appleTvPlus: "appletv-plus",
  appleTvSeasonPass: "appletv-season-pass",
};

export const appleProvidersType = {
  appleTv: "Apple TV",
  appleTvPlus: "Apple TV+",
  appleTvSeasonPass: "MLS Season Pass",
};

export const getClubsDir = () => {
  const clubsDir = window?.forgeVariables?.directoryList?.clubsDir || "clubs";

  return clubsDir;
};

export const removeLeadingSlash = (str) => str?.replace(/^\//, "") || "";
export const removeTrailingSlash = (str) => str?.replace(/\/$/, "") || "";

export function getHotelBookingIconUrl({ variant = "light" }) {
  const forgeMatchVariables = window?.forgeVariables?.match;
  const { hotelBookingIconLightUrl, hotelBookingIconDarkUrl } =
    forgeMatchVariables || {};

  return variant === "light"
    ? hotelBookingIconLightUrl
    : hotelBookingIconDarkUrl;
}

export function getTicketMasterIconUrl({ variant = "light" }) {
  const forgeMatchVariables = window?.forgeVariables?.match;
  const { ticketmasterIconDarkUrl, ticketmasterIconLightUrl } =
    forgeMatchVariables || {};

  return variant === "light"
    ? ticketmasterIconLightUrl
    : ticketmasterIconDarkUrl;
}

export function getTicketMasterUrl(urls = []) {
  return urls?.find((url) => url?.includes("ticketmaster"));
}
